/** Grid preset change history: a change list on the left, the selected
 *  change's before/after snapshots side-by-side on the right (read-only
 *  jsoneditor views), and a per-change "Restore this state" action that
 *  applies the snapshot the preset had BEFORE that change. */
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import * as presetsApi from '@/api/presets';
import { presetErrorMessage, type GridPresetChange } from '@/api/presets';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { JsonEditorWidget } from '@/components/JsonEditor/JsonEditorWidget';
import { useSnackbar } from '@/components/Snackbar';
import { formatRelativeTime } from '@/utils/format';

const ACTION_COLOR: Record<GridPresetChange['action'], 'success' | 'info' | 'error' | 'warning'> = {
  created: 'success',
  updated: 'info',
  deleted: 'error',
  restored: 'warning',
};

interface Props {
  presetId: number;
  onClose: () => void;
}

export function PresetHistoryDialog({ presetId, onClose }: Props) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [confirmRestore, setConfirmRestore] = useState<GridPresetChange | null>(null);

  const history = useQuery({
    queryKey: ['gridPresetHistory', presetId],
    queryFn: () => presetsApi.history(presetId, { limit: 200 }),
  });
  const changes = history.data?.results ?? [];
  const selected = changes.find((c) => c.id === selectedId) ?? changes[0] ?? null;

  const restoreMutation = useMutation({
    mutationFn: (changeId: number) => presetsApi.restore(presetId, changeId),
    onSuccess: (p) => {
      qc.setQueryData(['gridPreset', p.id], p);
      void qc.invalidateQueries({ queryKey: ['gridPresetsAll'] });
      void qc.invalidateQueries({ queryKey: ['gridPresets', p.grid] });
      void qc.invalidateQueries({ queryKey: ['gridPresetHistory', p.id] });
      snackbar.show(`Preset restored to its state before change #${confirmRestore?.id}.`, 'success');
      setConfirmRestore(null);
    },
    onError: (e: unknown) => {
      snackbar.show(presetErrorMessage(e), 'error');
      setConfirmRestore(null);
    },
  });

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Preset #{presetId} — change history</DialogTitle>
      <DialogContent>
        {history.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {history.isError && <Alert severity="error">Couldn’t load the history.</Alert>}
        {!history.isLoading && !history.isError && changes.length === 0 && (
          <Typography color="text.secondary">No recorded changes.</Typography>
        )}
        {changes.length > 0 && (
          <Stack direction="row" spacing={2} sx={{ minHeight: 420 }}>
            <List dense sx={{ width: 280, flexShrink: 0, overflow: 'auto', maxHeight: 480 }}>
              {changes.map((c) => (
                <ListItemButton
                  key={c.id}
                  dense
                  selected={c.id === selected?.id}
                  onClick={() => setSelectedId(c.id)}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Chip size="small" color={ACTION_COLOR[c.action]} label={c.action} />
                        <Typography variant="caption" color="text.secondary">
                          #{c.id}
                        </Typography>
                      </Box>
                    }
                    secondary={`${c.actor?.email ?? 'unknown'} · ${formatRelativeTime(c.created_ts)}`}
                    secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                  />
                </ListItemButton>
              ))}
            </List>
            {selected && (
              <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={2} sx={{ flex: 1 }}>
                  {(
                    [
                      ['Before', selected.state_before],
                      ['After', selected.state_after],
                    ] as const
                  ).map(([label, state]) => (
                    <Box key={label} sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary">
                        {label}
                      </Typography>
                      <Box sx={{ height: 380 }}>
                        {state ? (
                          // key forces a remount per change — the widget
                          // initializes once by design.
                          <JsonEditorWidget
                            key={`${selected.id}-${label}`}
                            initialValue={state}
                            readOnly
                            mode="tree"
                          />
                        ) : (
                          <Typography color="text.secondary" sx={{ p: 2 }}>
                            — none ({label === 'Before' ? 'creation' : 'deletion'}) —
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Stack>
                {selected.state_before !== null && (
                  <Box>
                    <Button size="small" onClick={() => setConfirmRestore(selected)}>
                      Restore the state before this change
                    </Button>
                  </Box>
                )}
              </Stack>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <ConfirmDialog
        open={confirmRestore !== null}
        title="Restore preset state?"
        message={`Apply the name/shared/settings the preset had before change #${confirmRestore?.id}? A new "restored" history entry will be recorded.`}
        confirmLabel="Restore"
        onConfirm={() => {
          if (confirmRestore) restoreMutation.mutate(confirmRestore.id);
        }}
        onCancel={() => setConfirmRestore(null)}
      />
    </Dialog>
  );
}
